import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Loader2, ArrowLeft, Video, Send, UserPlus, Copy, Trash2, Save, Plus, X, ListChecks,
  Lock, ShieldCheck, Link2, RotateCcw, Check,
} from "lucide-react";
import { MEETING_STATUSES, typeLabel, statusMeta, fmtDate } from "./meetingConstants";
import MeetingDocuments from "./MeetingDocuments";
import AgreementPanel from "./AgreementPanel";
import MeetingSummary from "./MeetingSummary";
import JitsiRoom from "./JitsiRoom";

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ guest_type: "temporary", name: "", email: "", organization: "", hours: 72 });
  const [issued, setIssued] = useState(null);
  const [newCheck, setNewCheck] = useState("");
  const [video, setVideo] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/meetings/${id}`); setD(data); }
    catch (e) { toast.error(errMsg(e, "Could not load meeting.")); }
    finally { setLoading(false); }
  }, [id]);
  const loadMsgs = useCallback(async () => {
    try { const { data } = await api.get(`/meetings/${id}/messages`); setMsgs(data.messages || []); } catch { /* noop */ }
  }, [id]);
  useEffect(() => { load(); loadMsgs(); }, [load, loadMsgs]);
  useEffect(() => { const t = setInterval(loadMsgs, 6000); return () => clearInterval(t); }, [loadMsgs]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const m = d?.meeting;
  const guests = d?.guests || [];
  const related = d?.related || {};

  const setStatus = async (status) => { try { await api.post(`/meetings/${id}/status`, { status }); toast.success("Status updated."); load(); } catch { toast.error("Failed."); } };
  const patch = async (fields, quiet) => { try { await api.put(`/meetings/${id}`, fields); if (!quiet) toast.success("Saved."); load(); } catch { toast.error("Save failed."); } };
  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try { await api.post(`/meetings/${id}/messages`, { text: reply.trim(), internal }); setReply(""); loadMsgs(); }
    catch { toast.error("Message failed."); } finally { setSending(false); }
  };
  const doInvite = async () => {
    try {
      const payload = {
        guest_type: invite.guest_type,
        name: invite.name.trim() || null,
        email: invite.email.trim() || null,
        organization: invite.organization.trim() || null,
        hours: Number(invite.hours) || 72,
      };
      const { data } = await api.post(`/meetings/${id}/invite-guest`, payload);
      setIssued({ ...data, link: `${window.location.origin}${data.path}` });
      load();
    } catch (e) { toast.error(errMsg(e, "Invite failed.")); }
  };
  const revokeGuest = async (gid) => { try { await api.post(`/meetings/${id}/guests/${gid}/revoke`); toast.success("Guest access revoked."); load(); } catch { toast.error("Failed."); } };
  const copy = (t) => { navigator.clipboard?.writeText(t).then(() => toast.success("Copied.")).catch(() => {}); };
  const joinVideo = async () => { try { const { data } = await api.get(`/meetings/${id}/video`); setVideo(data.video); } catch (e) { toast.error(errMsg(e, "Could not start video.")); } };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (!m) return <div className="py-24 text-center text-[#4B5563]">Meeting not found. <Button variant="link" onClick={() => navigate("/meetings")}>Back to meetings</Button></div>;

  const sm = statusMeta(m.status);
  const checklist = m.checklist || [];

  return (
    <div className="pb-16 max-w-6xl" data-testid="meeting-detail-page">
      <button onClick={() => navigate("/meetings")} className="inline-flex items-center gap-1.5 text-[13px] text-[#1C4358] hover:underline mb-3"><ArrowLeft size={15} /> All meetings</button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Video size={20} className="text-[#1C4358]" /><h1 className="text-xl font-display font-bold text-[#1C4358]">{m.title}</h1></div>
          <div className="text-[12px] text-[#4B5563] mt-1">{typeLabel(m.meeting_type)} · {fmtDate(m.scheduled_at)} · {m.duration_minutes} min · Host: {m.host_name}</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={m.status} onValueChange={setStatus}>
            <SelectTrigger data-testid="meeting-status-select" className="h-9 w-[180px]" style={{ borderColor: sm.hex, color: sm.hex }}><SelectValue /></SelectTrigger>
            <SelectContent>{MEETING_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: details + checklist + docs */}
        <div className="lg:col-span-2 space-y-4">
          {/* Video (Jitsi via provider abstraction) */}
          {video ? (
            <div data-testid="meeting-video-live">
              <JitsiRoom video={video} onLeave={() => setVideo(null)} />
              <button onClick={() => setVideo(null)} className="mt-1 text-[12px] text-[#9C1E22] hover:underline">Close video</button>
            </div>
          ) : (
            <Card className="p-6 border-[#E6EDF2] bg-[#0f2733] text-white text-center" data-testid="meeting-video-shell">
              <Video size={28} className="mx-auto mb-2 opacity-80" />
              <div className="font-display font-semibold text-[16px]">Video room ({m.provider})</div>
              <div className="text-[13px] text-white/70 mt-1">Recording is off. Everyone joins the same secure room.</div>
              <Button onClick={joinVideo} data-testid="meeting-join-video-btn" className="mt-3 bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Video size={15} /> Start / join video</Button>
              <div className="text-[11px] text-white/50 mt-2">Room: {m.room_name}</div>
            </Card>
          )}

          {/* Related + notes */}
          <Card className="p-4 border-[#E6EDF2]">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Details</div>
            <div className="flex flex-wrap gap-3 text-[13px] text-[#4B5563] mb-3">
              {related.lead && <button onClick={() => navigate(`/crm/leads/${related.lead.id}`)} className="underline text-[#1C4358]">Lead: {related.lead.name}</button>}
              {related.opportunity && <button onClick={() => navigate(`/crm/opportunities/${related.opportunity.id}`)} className="underline text-[#1C4358]">Opportunity: {related.opportunity.title}</button>}
              {m.service_lane && <span>Service lane: {m.service_lane}</span>}
            </div>
            <Label className="text-[12px] text-[#4B5563]">Meeting notes (internal)</Label>
            <Textarea data-testid="meeting-notes" defaultValue={m.notes || ""} rows={3} className="text-[14px]"
              onBlur={(e) => e.target.value !== (m.notes || "") && patch({ notes: e.target.value }, true)} />
          </Card>

          {/* Checklist */}
          <Card className="p-4 border-[#E6EDF2]" data-testid="meeting-checklist">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2"><ListChecks size={14} /> Next-step checklist</div>
            <div className="space-y-1.5">
              {checklist.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-[14px]">
                  <button onClick={() => { const next = checklist.map((x, j) => j === i ? { ...x, done: !x.done } : x); patch({ checklist: next }, true); }}
                    className={`h-5 w-5 rounded border flex items-center justify-center ${c.done ? "bg-[#2F7D5A] border-[#2F7D5A] text-white" : "border-[#D6E0E7]"}`}>{c.done ? <Check size={13} /> : null}</button>
                  <span className={c.done ? "line-through text-[#9AA5B1]" : "text-[#252221]"}>{c.label}</span>
                  <button onClick={() => patch({ checklist: checklist.filter((_, j) => j !== i) }, true)} className="ml-auto text-[#9C1E22]"><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={newCheck} onChange={(e) => setNewCheck(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newCheck.trim()) { patch({ checklist: [...checklist, { label: newCheck.trim(), done: false }] }, true); setNewCheck(""); } }} placeholder="Add a step…" className="h-9 text-[14px]" data-testid="meeting-checklist-input" />
              <Button size="sm" variant="outline" className="border-[#D6E0E7] text-[#1C4358]" onClick={() => { if (newCheck.trim()) { patch({ checklist: [...checklist, { label: newCheck.trim(), done: false }] }, true); setNewCheck(""); } }}><Plus size={14} /></Button>
            </div>
          </Card>

          {/* Documents + Agreement (Phase 10B) + AI Summary (Phase 10C) */}
          <MeetingDocuments meetingId={id} />
          <MeetingSummary meetingId={id} summary={m.summary} status={m.status} onChange={load} />
          <AgreementPanel meetingId={id} agreement={m.agreement} />
        </div>

        {/* Right: guests + chat */}
        <div className="space-y-4">
          {/* Guests */}
          <Card className="p-4 border-[#E6EDF2]" data-testid="meeting-guests">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70">Guests</div>
              <Button size="sm" data-testid="meeting-invite-btn" onClick={() => { setIssued(null); setInviteOpen(true); }} className="h-7 bg-[#1C4358] hover:bg-[#163545] text-white gap-1 text-[12px]"><UserPlus size={13} /> Invite</Button>
            </div>
            {guests.length === 0 ? <div className="text-[13px] text-[#9AA5B1]">No guests invited yet.</div> : (
              <div className="space-y-2">
                {guests.map((g) => (
                  <div key={g.id} className="flex items-center justify-between gap-2 text-[13px] border border-[#E6EDF2] rounded-lg px-2.5 py-1.5">
                    <div className="min-w-0">
                      <div className="font-medium text-[#1C4358] truncate">{g.name || "Guest"} <span className="text-[11px] text-[#9AA5B1]">· {g.guest_type}</span></div>
                      <div className="text-[11px] text-[#4B5563]">Code: <span className="font-mono">{g.access_code}</span> · {g.status}</div>
                    </div>
                    {g.status === "active" && <button onClick={() => revokeGuest(g.id)} className="text-[#9C1E22] shrink-0"><Trash2 size={14} /></button>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Room text chat */}
          <Card className="p-0 border-[#E6EDF2] flex flex-col" data-testid="meeting-chat" style={{ height: 420 }}>
            <div className="px-3 py-2 border-b border-[#E6EDF2] flex items-center gap-1.5 text-[13px] font-semibold text-[#1C4358]"><Send size={14} /> Room chat</div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
              {msgs.length === 0 && <div className="text-[13px] text-[#9AA5B1] text-center py-6">No messages yet.</div>}
              {msgs.map((mm) => (
                <div key={mm.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-[13px] ${mm.author_kind === "staff" ? "ml-auto bg-[#1C4358] text-white" : "bg-[#F1F5F8] text-[#252221]"} ${mm.internal ? "ring-1 ring-[#B7791F]/50" : ""}`}>
                  <div className="text-[10px] opacity-70 mb-0.5 flex items-center gap-1">{mm.internal && <Lock size={9} />} {mm.author_name} {mm.internal ? "· internal note" : ""}</div>
                  {mm.text}
                </div>
              ))}
            </div>
            <div className="border-t border-[#E6EDF2] p-2">
              <div className="flex items-center gap-2 mb-1.5">
                <Switch id="internal-toggle" checked={internal} onCheckedChange={setInternal} data-testid="meeting-internal-toggle" />
                <Label htmlFor="internal-toggle" className="text-[11px] text-[#B7791F] flex items-center gap-1"><Lock size={11} /> Internal note (staff only)</Label>
              </div>
              <div className="flex gap-2">
                <Input data-testid="meeting-chat-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={internal ? "Write an internal note…" : "Message the room…"} className="h-9 text-[14px]" />
                <Button size="sm" data-testid="meeting-chat-send" onClick={send} disabled={sending} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white">{sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}</Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="meeting-invite-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#1C4358]"><UserPlus size={18} /> Invite a guest</DialogTitle></DialogHeader>
          {!issued ? (
            <div className="space-y-3">
              <div>
                <Label className="text-[12px] text-[#4B5563]">Guest type</Label>
                <Select value={invite.guest_type} onValueChange={(v) => setInvite({ ...invite, guest_type: v })}>
                  <SelectTrigger data-testid="invite-type-select" className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="temporary">Temporary guest (family / client)</SelectItem><SelectItem value="professional">Professional guest (lawyer, realtor, partner)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-[12px] text-[#4B5563]">Name</Label><Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="h-10" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[12px] text-[#4B5563]">Email (optional)</Label><Input data-testid="invite-email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="h-10" /></div>
                <div><Label className="text-[12px] text-[#4B5563]">Link valid (hours)</Label><Input type="number" min={1} value={invite.hours} onChange={(e) => setInvite({ ...invite, hours: e.target.value })} className="h-10" /></div>
              </div>
              {invite.guest_type === "professional" && <div><Label className="text-[12px] text-[#4B5563]">Organization (optional)</Label><Input value={invite.organization} onChange={(e) => setInvite({ ...invite, organization: e.target.value })} className="h-10" /></div>}
            </div>
          ) : (
            <div className="space-y-3" data-testid="invite-issued">
              <div className="rounded-lg bg-[#FBF6EC] border border-[#F0E6CF] p-3">
                <div className="text-[12px] text-[#4B5563] mb-1">Share this secure link with the guest:</div>
                <div className="flex items-center gap-2"><Input readOnly value={issued.link} className="h-9 text-[12px] font-mono" data-testid="invite-link" /><Button size="sm" variant="outline" onClick={() => copy(issued.link)} className="border-[#D6E0E7] text-[#1C4358]"><Copy size={14} /></Button></div>
                <div className="mt-2 text-[12px] text-[#4B5563]">One-time access code:</div>
                <div className="flex items-center gap-2"><span className="text-[22px] font-bold tracking-widest text-[#1C4358] font-mono" data-testid="invite-code">{issued.access_code}</span><Button size="sm" variant="outline" onClick={() => copy(issued.access_code)} className="border-[#D6E0E7] text-[#1C4358]"><Copy size={14} /></Button></div>
              </div>
              <p className="flex items-start gap-1.5 text-[12px] text-[#4B5563]"><ShieldCheck size={13} className="text-[#2F7D5A] mt-0.5" /> The guest can only see this room. The link expires automatically.</p>
            </div>
          )}
          <DialogFooter>
            {!issued ? (
              <><Button variant="outline" onClick={() => setInviteOpen(false)} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button>
              <Button data-testid="invite-submit" onClick={doInvite} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Link2 size={14} /> Create secure link</Button></>
            ) : (
              <Button onClick={() => setInviteOpen(false)} className="bg-[#1C4358] text-white">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
